import React, { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Link, useHistory } from 'react-router-dom'
import { IconButton, makeStyles, Tooltip } from '@material-ui/core'
import VisibilityIcon from '@material-ui/icons/Visibility'
import AdminPage from '../../../admin/shared/AdminPage'
import SydButton from '../../../../commonDesign/Button'
import OperationalTable from '../../../../organisms/OperationalTable'
import Loading from '../../../../molecules/Loading'
import { useListAiEvalRuns } from '../../../../../api/denaliAssistant'
import { formatRelativeTime } from '../../Analysis/SmartEndpoints/formatRelativeTime'
import StatusBadge from './shared/StatusBadge'
import { scoreColor } from './shared/scoreColor'

const POLL_MS = 5000

const useStyles = makeStyles(() => ({
  toolbar: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '8px var(--padding-md)',
    borderBottom: '1px solid var(--color-neutral-200)',
    backgroundColor: 'var(--color-neutral-050)'
  },
  count: {
    fontSize: 'var(--text-size-xs)',
    fontWeight: 600,
    color: 'var(--color-neutral-400)',
    textTransform: 'uppercase',
    letterSpacing: '0.4px'
  },
  searchInput: {
    padding: '5px 10px',
    border: '1px solid var(--color-neutral-300)',
    borderRadius: 'var(--border-radius-sm)',
    fontSize: 'var(--text-size-xs)',
    outline: 'none',
    width: '200px',
    backgroundColor: 'white',
    color: 'var(--color-neutral-900)'
  },
  tableWrap: {
    '& .operational-table > thead > tr > th': {
      fontSize: 'var(--text-size-xs)',
      fontWeight: 600,
      textTransform: 'uppercase',
      letterSpacing: '0.5px',
      color: 'var(--color-neutral-500)',
      padding: '10px 18px',
      borderBottom: '1px solid var(--color-neutral-200)'
    },
    '& .operational-table > thead': { backgroundColor: 'var(--color-neutral-050)' },
    '& .operational-table > tbody > tr > td': { padding: '10px 18px' }
  },
  mono: {
    fontFamily: 'var(--font-mono, monospace)',
    fontSize: 'var(--text-size-xs)'
  },
  promptCell: {
    maxWidth: '420px',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    color: 'var(--color-neutral-700)',
    fontSize: 'var(--text-size-xs)'
  },
  timestampText: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-400)'
  },
  scoreCell: {
    fontWeight: 600,
    fontVariantNumeric: 'tabular-nums'
  },
  progress: {
    fontSize: 'var(--text-size-xs)',
    color: 'var(--color-neutral-600)'
  },
  actionBtn: {
    color: 'var(--color-neutral-300)',
    transition: 'color 0.15s ease',
    '&:hover': {
      color: 'var(--color-primary-600)',
      backgroundColor: 'var(--color-primary-050, #eff6ff)'
    }
  },
  actionsCell: {
    display: 'flex',
    gap: '2px'
  },
  newBtnWrap: {
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '6px 0'
  },
  empty: {
    padding: '48px 20px',
    textAlign: 'center'
  },
  emptyText: {
    fontSize: 'var(--text-size-sm)',
    color: 'var(--color-neutral-500)',
    marginBottom: '24px',
    lineHeight: 1.7
  }
}))

const DEFAULT_SORT = [{ id: 'batchId', desc: true }]

/* eslint-disable react/prop-types */
const buildColumns = (classes, history, root) => [
  {
    id: 'batchId',
    accessor: 'batchId',
    Header: 'ID',
    Cell: ({ value }) => <span className={classes.mono}>#{value}</span>
  },
  {
    id: 'name',
    accessor: row => row.name || row.prompt,
    Header: 'Name / Prompt',
    Cell: ({ row }) => (
      <div className={classes.promptCell}>
        {row.original.name || row.original.prompt || '(no prompt)'}
      </div>
    )
  },
  {
    id: 'status',
    accessor: 'status',
    Header: 'Status',
    Cell: ({ value }) => <StatusBadge status={value} />
  },
  {
    id: 'progress',
    Header: 'Progress',
    Cell: ({ row }) => {
      const r = row.original
      return (
        <span className={classes.progress}>
          {r.completedCount}/{r.taskCount || r.sampleCount}
          {r.errorCount ? ` \u00B7 ${r.errorCount} err` : ''}
        </span>
      )
    }
  },
  {
    id: 'meanWeightedScore',
    accessor: 'meanWeightedScore',
    Header: 'Score',
    Cell: ({ value }) => {
      if (value === null || value === undefined) {
        return <span className={classes.timestampText}>{'\u2014'}</span>
      }
      const pct = value * 100
      return (
        <span className={classes.scoreCell} style={{ color: scoreColor(pct) }}>
          {pct.toFixed(1)}%
        </span>
      )
    }
  },
  {
    id: 'createdBy',
    accessor: 'createdBy',
    Header: 'Created By',
    Cell: ({ value }) => <span className={classes.timestampText}>{value || '\u2014'}</span>
  },
  {
    id: 'lastStartedDate',
    accessor: 'lastStartedDate',
    Header: 'Started',
    Cell: ({ value }) => {
      if (!value) return <span className={classes.timestampText}>{'\u2014'}</span>
      return (
        <Tooltip title={new Date(value).toLocaleString()} arrow>
          <span className={classes.timestampText}>{formatRelativeTime(value)}</span>
        </Tooltip>
      )
    }
  },
  {
    id: 'actions',
    Header: '',
    disableSortBy: true,
    Cell: ({ row }) => (
      <div className={classes.actionsCell}>
        <Tooltip title='View run' arrow>
          <IconButton
            size='small'
            className={classes.actionBtn}
            onClick={() => history.push(`${root}/ai/evals/${row.original.batchId}`)}
          >
            <VisibilityIcon fontSize='small' />
          </IconButton>
        </Tooltip>
      </div>
    )
  }
]
/* eslint-enable react/prop-types */

function NewEvalButton ({ root }) {
  return (
    <Link to={`${root}/ai/evals/new`}>
      <SydButton size='sm' variant='primary' icon='add'>New Eval</SydButton>
    </Link>
  )
}

NewEvalButton.propTypes = { root: PropTypes.string.isRequired }

function EmptyState ({ root }) {
  const classes = useStyles()
  return (
    <div className={classes.empty}>
      <div className={classes.emptyText}>
        No eval runs yet. Create one to compare assistant responses against a rubric.
      </div>
      <NewEvalButton root={root} />
    </div>
  )
}

EmptyState.propTypes = { root: PropTypes.string.isRequired }

function EvalsHome ({ root }) {
  const classes = useStyles()
  const history = useHistory()
  const [searchText, setSearchText] = useState('')

  const { data, isLoading } = useListAiEvalRuns({ refetchInterval: POLL_MS })
  const tableData = useMemo(() => data?.runs || [], [data])

  const columns = useMemo(
    () => buildColumns(classes, history, root),
    [classes, history, root]
  )

  if (isLoading) {
    return (
      <AdminPage>
        <Loading />
      </AdminPage>
    )
  }

  if (tableData.length === 0) {
    return (
      <AdminPage>
        <EmptyState root={root} />
      </AdminPage>
    )
  }

  return (
    <AdminPage>
      <div className={classes.newBtnWrap}>
        <NewEvalButton root={root} />
      </div>

      <div className={classes.toolbar}>
        <span className={classes.count}>
          {tableData.length} {tableData.length === 1 ? 'run' : 'runs'}
        </span>
        <input
          className={classes.searchInput}
          placeholder={'Search runs\u2026'}
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
        />
      </div>

      <div className={classes.tableWrap}>
        <OperationalTable
          variant='blank'
          itemName='Eval Runs'
          columns={columns}
          data={tableData}
          defaultSort={DEFAULT_SORT}
          searchText={searchText}
        />
      </div>
    </AdminPage>
  )
}

EvalsHome.propTypes = {
  root: PropTypes.string
}

export default EvalsHome
